import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Lock } from 'lucide-react'
import type { Page, Role } from '@b4l/shared'
import { ROLES } from '@b4l/shared'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { AntetFix } from '../../components/ui/AntetFix'
import { Button } from '../../components/ui/Button'
import { SearchSelect } from '../../components/ui/SearchSelect'
import { useAuthStore } from '../../stores/auth'

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  administrativ: 'Operațional',
  marketing: 'Marketing',
  depozit: 'Depozit',
  contabilitate: 'Contabilitate',
  vanzari: 'Vânzări',
  manager_vanzari: 'Manager vânzări',
}

// Ordinea grupurilor în UI (ca în meniu). Grupuri necunoscute apar la final.
const GROUP_ORDER = [
  'Acasă',
  'Vânzări',
  'Magazine online',
  'Marketing',
  'Bani',
  'Operațiuni',
  'Firmă & oameni',
  'Setări',
]

const EDITABLE_ROLES = ROLES.filter((r) => r !== 'admin')

function groupPages(pages: Page[]): { group: string; pages: Page[] }[] {
  const byGroup = new Map<string, Page[]>()
  for (const p of pages) {
    const arr = byGroup.get(p.group) ?? []
    arr.push(p)
    byGroup.set(p.group, arr)
  }
  return [...byGroup.entries()]
    .sort((a, b) => {
      const ia = GROUP_ORDER.indexOf(a[0])
      const ib = GROUP_ORDER.indexOf(b[0])
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
    })
    .map(([group, pages]) => ({ group, pages }))
}

interface CatalogResp {
  pages: Page[]
}
interface RolesResp {
  roles: Record<string, string[]>
}
interface UserAccessResp {
  user: { id: number; name: string; email: string; role: Role }
  isAdmin: boolean
  inherited: string[]
  grants: string[]
  revokes: string[]
  effective: string[]
  permisiuniAcordabile: { permission: string; label: string }[]
  permisiuniAcordate: { permission: string; note: string | null; label: string }[]
}
interface UserRow {
  id: number
  email: string
  name: string
  role: Role
  active: boolean
}

export function SetariRoluriAccesPage() {
  const [tab, setTab] = useState<'rol' | 'user'>('rol')

  const catalogQuery = useQuery<CatalogResp>({
    queryKey: ['access', 'catalog'],
    queryFn: async () => (await api.get<CatalogResp>('/settings/access/catalog')).data,
  })
  const grouped = useMemo(() => groupPages(catalogQuery.data?.pages ?? []), [catalogQuery.data])

  return (
    <div className="space-y-4">
      <AntetFix>
        <div>
          <h1 className="text-xl font-semibold">Roluri &amp; acces</h1>
          <p className="mt-1 text-sm text-b4l-text-muted">
            Controlează ce pagini vede fiecare rol și acordă/retrage excepții per utilizator.
            Adminul are întotdeauna acces complet.
          </p>
        </div>

        <div className="flex gap-1 border-b border-b4l-border">
          <TabBtn active={tab === 'rol'} onClick={() => setTab('rol')}>
            Pe rol
          </TabBtn>
          <TabBtn active={tab === 'user'} onClick={() => setTab('user')}>
            Pe utilizator
          </TabBtn>
        </div>
      </AntetFix>

      {catalogQuery.isLoading ? (
        <Card>
          <p className="text-sm text-b4l-text-muted">Se încarcă catalogul…</p>
        </Card>
      ) : tab === 'rol' ? (
        <RoleMatrix grouped={grouped} />
      ) : (
        <UserOverrides grouped={grouped} />
      )}
    </div>
  )
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition',
        active
          ? 'border-b4l-blue text-b4l-blue'
          : 'border-transparent text-b4l-text-muted hover:text-b4l-text',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/* ---------------------------------------------------------------- Tab „Pe rol" */

function RoleMatrix({ grouped }: { grouped: { group: string; pages: Page[] }[] }) {
  const queryClient = useQueryClient()
  const rolesQuery = useQuery<RolesResp>({
    queryKey: ['access', 'roles'],
    queryFn: async () => (await api.get<RolesResp>('/settings/access/roles')).data,
  })

  // Draft editabil: rol -> Set(pageKey). Reinit când vin datele de pe server.
  const [draft, setDraft] = useState<Record<string, Set<string>>>({})
  const server = rolesQuery.data?.roles

  useEffect(() => {
    if (!server) return
    const next: Record<string, Set<string>> = {}
    for (const r of EDITABLE_ROLES) next[r] = new Set(server[r] ?? [])
    setDraft(next)
  }, [server])

  const dirtyRoles = useMemo(() => {
    if (!server) return [] as Role[]
    return EDITABLE_ROLES.filter((r) => {
      const cur = draft[r] ?? new Set()
      const orig = new Set(server[r] ?? [])
      if (cur.size !== orig.size) return true
      for (const k of cur) if (!orig.has(k)) return true
      return false
    })
  }, [draft, server])

  const saveMutation = useMutation({
    mutationFn: async (roles: Role[]) => {
      for (const role of roles) {
        await api.put(`/settings/access/roles/${role}`, {
          pageKeys: [...(draft[role] ?? new Set())],
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access', 'roles'] })
      toast.success('Acces pe rol actualizat')
    },
    onError: (err: unknown) => toast.error(errMsg(err)),
  })

  const toggle = (role: Role, pageKey: string) => {
    setDraft((prev) => {
      const set = new Set(prev[role] ?? [])
      if (set.has(pageKey)) set.delete(pageKey)
      else set.add(pageKey)
      return { ...prev, [role]: set }
    })
  }

  if (rolesQuery.isLoading) {
    return (
      <Card>
        <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-b4l-text-muted">
          Bifează paginile accesibile pentru fiecare rol. Coloana Admin e fixă (acces complet).
        </p>
        <Button
          onClick={() => saveMutation.mutate(dirtyRoles)}
          disabled={dirtyRoles.length === 0}
          loading={saveMutation.isPending}
        >
          {dirtyRoles.length > 0
            ? `Salvează (${dirtyRoles.length} ${dirtyRoles.length === 1 ? 'rol' : 'roluri'})`
            : 'Nimic de salvat'}
        </Button>
      </div>

      {/*
        Tabelul derulează în propria casetă (nu odată cu pagina), ca antetul cu rolurile
        să poată rămâne fix: `sticky` se raportează la containerul care derulează, deci
        acesta trebuie să aibă înălțime limitată și `overflow-auto`.
      */}
      <Card className="max-h-[calc(100vh-15rem)] overflow-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-b4l-text-muted">
              <th className="sticky top-0 z-10 border-b border-b4l-border bg-white py-2 pr-3 text-left font-medium">
                Pagină
              </th>
              {EDITABLE_ROLES.map((r) => (
                <th
                  key={r}
                  className="sticky top-0 z-10 border-b border-b4l-border bg-white px-2 py-2 text-center font-medium"
                >
                  {ROLE_LABELS[r]}
                  {rolesQuery.data && dirtyRoles.includes(r) && (
                    <span className="ml-1 text-b4l-blue">•</span>
                  )}
                </th>
              ))}
              <th className="sticky top-0 z-10 border-b border-b4l-border bg-white px-2 py-2 text-center font-medium text-b4l-text-muted">
                Admin
              </th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(({ group, pages }) => (
              <Fragment key={group}>
                <tr className="bg-b4l-bg-soft">
                  <td
                    colSpan={EDITABLE_ROLES.length + 2}
                    className="py-1.5 pl-1 text-xs font-semibold uppercase tracking-wide text-b4l-text-muted"
                  >
                    {group}
                  </td>
                </tr>
                {pages.map((p) => (
                  <tr key={p.key} className="border-b border-b4l-border last:border-0">
                    <td className="py-1.5 pr-3">{p.label}</td>
                    {EDITABLE_ROLES.map((r) => (
                      <td key={r} className="px-2 py-1.5 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 cursor-pointer accent-b4l-blue"
                          checked={draft[r]?.has(p.key) ?? false}
                          onChange={() => toggle(r, p.key)}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-center">
                      <span
                        className="font-semibold text-b4l-green-dark"
                        title="Adminul are întotdeauna acces complet"
                      >
                        ✓
                      </span>
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

/* -------------------------------------------------------- Tab „Pe utilizator" */

function UserOverrides({ grouped }: { grouped: { group: string; pages: Page[] }[] }) {
  const queryClient = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const [userId, setUserId] = useState<number | null>(null)

  const usersQuery = useQuery<{ users: UserRow[] }>({
    queryKey: ['settings', 'users'],
    queryFn: async () => (await api.get<{ users: UserRow[] }>('/settings/users')).data,
  })
  const userOptions = useMemo(
    () =>
      (usersQuery.data?.users ?? [])
        .filter((u) => u.active)
        .map((u) => ({
          value: u.id,
          label: `${u.name} · ${u.email} — ${ROLE_LABELS[u.role]}`,
        })),
    [usersQuery.data],
  )

  const accessQuery = useQuery<UserAccessResp>({
    queryKey: ['access', 'user', userId],
    queryFn: async () => (await api.get<UserAccessResp>(`/settings/access/users/${userId}`)).data,
    enabled: userId != null,
  })

  // Draft = setul EFECTIV de pagini pentru user (moștenit ± excepții). Reinit la fetch.
  const [effective, setEffective] = useState<Set<string>>(new Set())
  const inherited = useMemo(() => new Set(accessQuery.data?.inherited ?? []), [accessQuery.data])

  useEffect(() => {
    if (accessQuery.data) setEffective(new Set(accessQuery.data.effective))
  }, [accessQuery.data])

  // Excepțiile derivate: diferența dintre efectivul dorit și moștenirea din rol.
  const overrides = useMemo(() => {
    const all = new Set<string>([...inherited, ...effective])
    const out: { pageKey: string; allowed: boolean }[] = []
    for (const key of all) {
      const base = inherited.has(key)
      const eff = effective.has(key)
      if (base !== eff) out.push({ pageKey: key, allowed: eff })
    }
    return out
  }, [inherited, effective])

  const savedOverrides = useMemo(() => {
    const g = accessQuery.data?.grants ?? []
    const r = accessQuery.data?.revokes ?? []
    return g.length + r.length
  }, [accessQuery.data])

  const dirty = useMemo(() => {
    // Compară overrides curente cu cele salvate.
    const savedSet = new Set<string>([
      ...(accessQuery.data?.grants ?? []).map((k) => `+${k}`),
      ...(accessQuery.data?.revokes ?? []).map((k) => `-${k}`),
    ])
    const curSet = new Set(overrides.map((o) => `${o.allowed ? '+' : '-'}${o.pageKey}`))
    if (savedSet.size !== curSet.size) return true
    for (const k of curSet) if (!savedSet.has(k)) return true
    return false
  }, [overrides, accessQuery.data])

  const saveMutation = useMutation({
    mutationFn: async () => (await api.put(`/settings/access/users/${userId}`, { overrides })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access', 'user', userId] })
      toast.success('Excepții actualizate')
    },
    onError: (err: unknown) => toast.error(errMsg(err)),
  })

  const toggle = (pageKey: string) => {
    setEffective((prev) => {
      const set = new Set(prev)
      if (set.has(pageKey)) set.delete(pageKey)
      else set.add(pageKey)
      return set
    })
  }

  const resetToRole = () => setEffective(new Set(inherited))

  const isSelf = me?.id === userId
  const isAdmin = accessQuery.data?.isAdmin

  return (
    <div className="space-y-3">
      <Card>
        <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Utilizator</label>
        <div className="max-w-md">
          <SearchSelect
            options={userOptions}
            value={userId}
            onChange={setUserId}
            placeholder="— alege utilizator —"
          />
        </div>
      </Card>

      {userId == null ? (
        <Card>
          <p className="text-sm text-b4l-text-muted">
            Alege un utilizator ca să vezi și să ajustezi accesul lui.
          </p>
        </Card>
      ) : accessQuery.isLoading ? (
        <Card>
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        </Card>
      ) : isSelf ? (
        <Card>
          <p className="text-sm text-b4l-amber-dark">
            Nu-ți poți edita propriul acces. Alege alt utilizator.
          </p>
        </Card>
      ) : isAdmin ? (
        /*
          Ecranul se oprea AICI, cu un singur rând de text. Dar drepturile PERSONALE sunt
          exact cele care se aplică unui admin — nu vin cu rolul, sunt scăzute din blanket și
          se dau punctual. Victor e admin, iar dreptul lui de export nu se vedea nicăieri:
          Alexandru, 30.09.2026, uitându-se fix la el — „nu apare nimic aici".

          Excepțiile de PAGINĂ chiar nu se aplică; propoziția de mai jos spune acum care e
          diferența, în loc să le pună pe toate la un loc.
        */
        <>
          <Card>
            <p className="text-sm text-b4l-text-muted">
              <strong>{accessQuery.data?.user.name}</strong> este Admin — vede toate paginile, deci
              excepțiile de acces nu se aplică. Drepturile personale de mai jos <em>se aplică</em>:
              ele nu vin cu rolul.
            </p>
          </Card>
          <DrepturiModificare userId={userId} date={accessQuery.data!} />
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-b4l-text-muted">
              Bifat gri = moștenit din rolul{' '}
              <strong>{ROLE_LABELS[accessQuery.data!.user.role]}</strong>. Bifează în plus (
              <span className="text-b4l-green-dark">adăugat</span>) sau scoate (
              <span className="text-b4l-red-dark">scos</span>) față de rol.
              {savedOverrides > 0 && ` · ${savedOverrides} excepții salvate`}
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={resetToRole} disabled={overrides.length === 0}>
                Revino la rol
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!dirty}
                loading={saveMutation.isPending}
              >
                Salvează excepțiile
              </Button>
            </div>
          </div>

          <Card className="max-h-[calc(100vh-15rem)] overflow-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="sticky top-0 z-10 border-b border-b4l-border bg-white py-2 pr-3 text-left font-medium">
                    Pagină
                  </th>
                  <th className="sticky top-0 z-10 border-b border-b4l-border bg-white px-2 py-2 text-center font-medium">
                    Acces
                  </th>
                  <th className="sticky top-0 z-10 border-b border-b4l-border bg-white px-2 py-2 text-left font-medium">
                    Stare
                  </th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(({ group, pages }) => (
                  <Fragment key={group}>
                    <tr className="bg-b4l-bg-soft">
                      <td
                        colSpan={3}
                        className="py-1.5 pl-1 text-xs font-semibold uppercase tracking-wide text-b4l-text-muted"
                      >
                        {group}
                      </td>
                    </tr>
                    {pages.map((p) => {
                      const base = inherited.has(p.key)
                      const eff = effective.has(p.key)
                      const changed = base !== eff
                      return (
                        <tr key={p.key} className="border-b border-b4l-border last:border-0">
                          <td className="py-1.5 pr-3">{p.label}</td>
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 cursor-pointer accent-b4l-blue"
                              checked={eff}
                              onChange={() => toggle(p.key)}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            {changed && eff && (
                              <span className="rounded bg-b4l-green-light px-2 py-0.5 text-[10px] font-medium text-b4l-green-dark">
                                adăugat
                              </span>
                            )}
                            {changed && !eff && (
                              <span className="rounded bg-b4l-red-light px-2 py-0.5 text-[10px] font-medium text-b4l-red-dark">
                                scos
                              </span>
                            )}
                            {!changed && base && (
                              <span className="text-[10px] text-b4l-text-muted">din rol</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </Card>

          <DrepturiModificare userId={userId} date={accessQuery.data!} />
        </>
      )}
    </div>
  )
}

/* ------------------------------------------- Drepturi de modificare, per utilizator */

/**
 * Accesul pe pagină spune ce vede omul. Aici se spune ce poate schimba.
 *
 * Fără caseta asta, singura pârghie era rolul: ca să lași un om să mute clienți între agenți
 * trebuia să-l faci admin, adică să-i dai tot. Acum i se dă exact atât.
 */
function DrepturiModificare({ userId, date }: { userId: number; date: UserAccessResp }) {
  const queryClient = useQueryClient()
  const [ales, setAles] = useState<Set<string>>(new Set())

  useEffect(() => {
    setAles(new Set(date.permisiuniAcordate.map((p) => p.permission)))
  }, [date])

  const salvat = useMemo(
    () => new Set(date.permisiuniAcordate.map((p) => p.permission)),
    [date.permisiuniAcordate],
  )
  const dirty = useMemo(() => {
    if (ales.size !== salvat.size) return true
    for (const p of ales) if (!salvat.has(p)) return true
    return false
  }, [ales, salvat])

  const saveMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put(`/settings/access/users/${userId}/permissions`, {
          permissions: [...ales].map((permission) => ({ permission })),
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access', 'user', userId] })
      toast.success('Drepturi actualizate')
    },
    onError: (err: unknown) => toast.error(errMsg(err)),
  })

  const toggle = (permission: string) => {
    setAles((prev) => {
      const next = new Set(prev)
      if (next.has(permission)) next.delete(permission)
      else next.add(permission)
      return next
    })
  }

  /*
    Drepturile pe care omul LE ARE, dar pe care cel care se uită nu le poate schimba.

    Un drept personal se acordă doar de cine îl are deja. Până acum asta însemna și că nu se
    VEDEA: lista de bife e construită din ce poți acorda, deci pentru toți ceilalți dreptul lui
    Victor nu exista pe ecran. Un drept pe care nu-l poate vedea nimeni nu se poate verifica —
    iar Victor a cerut să fie singurul care îl poate DA mai departe, nu să nu se știe că-l are.

    Se afișează fără bifă, cu lacăt. Salvarea nu le atinge: `ales` pornește din ce e acordat,
    iar serverul refuză oricum retragerea lor de către cine nu le are.
  */
  const acordabile = new Set(date.permisiuniAcordabile.map((p) => p.permission))
  const doarVizibile = date.permisiuniAcordate.filter((p) => !acordabile.has(p.permission))

  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">Drepturi de modificare</h2>
          <p className="mt-0.5 text-xs text-b4l-text-muted">
            Accesul de mai sus decide ce <em>vede</em>. Aici decizi ce poate <em>schimba</em>, doar
            el, peste rolul lui. Lista urmează paginile deja salvate — dacă tocmai ai bifat o pagină
            nouă, salveaz-o întâi.
          </p>
        </div>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!dirty}
          loading={saveMutation.isPending}
        >
          Salvează drepturile
        </Button>
      </div>

      {doarVizibile.length > 0 && (
        <ul className="divide-y divide-b4l-border/60 rounded-lg bg-b4l-bg px-3">
          {doarVizibile.map((p) => (
            <li key={p.permission} className="flex items-start gap-3 py-2">
              <Lock size={14} className="mt-0.5 shrink-0 text-b4l-text-dim" aria-hidden />
              <span className="min-w-0 flex-1 text-sm">
                <span className="block break-words">{p.label}</span>
                <span className="block break-all text-[11px] text-b4l-text-dim">
                  {p.permission}
                </span>
              </span>
              <span className="shrink-0 rounded bg-b4l-green-light px-2 py-0.5 text-[10px] font-medium text-b4l-green-dark">
                acordat
              </span>
            </li>
          ))}
          <li className="py-2 text-[11px] text-b4l-text-muted">
            Drepturi pe care le are, dar pe care nu le poți schimba tu: un drept personal se acordă
            și se retrage doar de cine îl are el însuși.
          </li>
        </ul>
      )}

      {date.permisiuniAcordabile.length === 0 ? (
        doarVizibile.length === 0 && (
          <p className="text-sm text-b4l-text-muted">
            {date.isAdmin
              ? 'Niciun drept personal acordat. Adminul le are pe toate celelalte din rol.'
              : `Nu e nimic de acordat: pe paginile lui, rolul ${ROLE_LABELS[date.user.role]} poate deja tot ce se poate modifica.`}
          </p>
        )
      ) : (
        <ul className="divide-y divide-b4l-border/60">
          {date.permisiuniAcordabile.map((p) => (
            <li key={p.permission} className="flex items-start gap-3 py-2">
              <input
                type="checkbox"
                id={`perm-${p.permission}`}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-b4l-blue"
                checked={ales.has(p.permission)}
                onChange={() => toggle(p.permission)}
              />
              <label
                htmlFor={`perm-${p.permission}`}
                className="min-w-0 flex-1 cursor-pointer text-sm"
              >
                <span className="block break-words">{p.label}</span>
                <span className="block break-all text-[11px] text-b4l-text-dim">
                  {p.permission}
                </span>
              </label>
              {salvat.has(p.permission) && (
                <span className="shrink-0 rounded bg-b4l-green-light px-2 py-0.5 text-[10px] font-medium text-b4l-green-dark">
                  acordat
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function errMsg(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  )
}
